import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  CalendarDays, ListTodo, Link2, NotebookPen, StickyNote, Users,
  Plus, Check, Trash2, ArrowRight, Sparkles, Clock,
} from "lucide-react";
import { Widget, WidgetHeader, EmptyState } from "@/components/Widget";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { useCollection } from "@/hooks/useCollection";
import { api } from "@/lib/api";

const todayStr = new Date().toISOString().slice(0, 10);
const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

export default function Dashboard() {
  const { user } = useAuth();
  const events = useCollection("events");
  const reminders = useCollection("reminders");
  const resources = useCollection("resources");
  const lessons = useCollection("lesson-plans");
  const notes = useCollection("notes");
  const students = useCollection("students");
  const [newReminder, setNewReminder] = useState("");

  const prefs = user?.preferences || {};
  const visibleIds = (prefs.widgets || []).filter((w) => w.visible).map((w) => w.id);
  const show = (id) => visibleIds.length === 0 || visibleIds.includes(id);

  const todayEvents = events.items
    .filter((e) => e.date === todayStr)
    .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
  const openReminders = reminders.items.filter((r) => !r.done);

  const addReminder = async (e) => {
    e.preventDefault();
    if (!newReminder.trim()) return;
    await reminders.create({ text: newReminder.trim(), done: false });
    setNewReminder("");
    toast.success("Reminder added");
  };

  const toggleReminder = async (r) => {
    await reminders.update(r.id, { ...r, done: !r.done });
  };

  return (
    <div>
      <div className="mb-8">
        <p className="text-primary font-semibold text-sm tracking-wide">{new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold text-slate-800 mt-1" data-testid="dashboard-greeting">
          {greeting()}, {user?.name?.split(" ")[0]} ✨
        </h1>
        <p className="text-slate-500 mt-1">Here's your whole day, in one calm place.</p>
      </div>

      {/* Stat row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={CalendarDays} label="Events today" value={todayEvents.length} tid="stat-events" />
        <StatCard icon={ListTodo} label="Open reminders" value={openReminders.length} tid="stat-reminders" />
        <StatCard icon={NotebookPen} label="Lesson plans" value={lessons.items.length} tid="stat-lessons" />
        <StatCard icon={Users} label="Students" value={students.items.length} tid="stat-students" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-6">
        {/* Today's agenda */}
        {show("agenda") && (
          <Widget className="col-span-full md:col-span-2 lg:col-span-4" testId="widget-agenda">
            <WidgetHeader icon={CalendarDays} title="Today's Agenda"
              action={<Link to="/calendar"><Button variant="ghost" size="sm" className="rounded-full text-primary" data-testid="agenda-view-all">Calendar <ArrowRight size={15} className="ml-1" /></Button></Link>} />
            {todayEvents.length === 0 ? (
              <EmptyState>No events today. Enjoy the calm — or add one from the Calendar.</EmptyState>
            ) : (
              <div className="space-y-2">
                {todayEvents.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 p-3 rounded-2xl bg-slate-50" data-testid="agenda-event">
                    <span className="h-10 w-1.5 rounded-full" style={{ background: e.color || "#8FA9E3" }} />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{e.title}</p>
                      {e.location && <p className="text-xs text-slate-400">{e.location}</p>}
                    </div>
                    {(e.start_time || e.end_time) && (
                      <div className="flex items-center gap-1 text-sm text-slate-500 shrink-0">
                        <Clock size={14} />{e.start_time}{e.end_time ? `–${e.end_time}` : ""}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Widget>
        )}

        {/* Reminders */}
        {show("reminders") && (
          <Widget className="col-span-full md:col-span-2" delay={0.05} testId="widget-reminders">
            <WidgetHeader icon={ListTodo} title="Reminders" />
            <form onSubmit={addReminder} className="flex gap-2 mb-4">
              <Input value={newReminder} onChange={(e) => setNewReminder(e.target.value)} placeholder="Quick reminder…"
                data-testid="reminder-input" className="rounded-xl bg-slate-50 border-slate-200 h-10" />
              <Button type="submit" size="icon" data-testid="reminder-add-button" className="rounded-xl bg-primary hover:bg-primary/90 shrink-0"><Plus size={18} /></Button>
            </form>
            <div className="space-y-2 max-h-56 overflow-y-auto">
              {openReminders.length === 0 && <EmptyState>All caught up!</EmptyState>}
              {openReminders.map((r) => (
                <div key={r.id} className="flex items-center gap-2.5 group" data-testid="reminder-item">
                  <button onClick={() => toggleReminder(r)} data-testid="reminder-toggle"
                    className="h-5 w-5 rounded-md border-2 border-slate-300 hover:border-primary flex items-center justify-center shrink-0 transition-colors">
                  </button>
                  <span className="flex-1 text-sm text-slate-700">{r.text}</span>
                  <button onClick={() => reminders.remove(r.id)} className="opacity-0 group-hover:opacity-100 text-slate-300 hover:text-destructive transition"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </Widget>
        )}

        {/* Quick links */}
        {show("quicklinks") && (
          <Widget className="col-span-full md:col-span-2 lg:col-span-3" delay={0.1} testId="widget-quicklinks">
            <WidgetHeader icon={Link2} title="Quick Links"
              action={<Link to="/resources"><Button variant="ghost" size="sm" className="rounded-full text-primary" data-testid="quicklinks-manage">Manage</Button></Link>} />
            {resources.items.length === 0 ? (
              <EmptyState>Add your favorite school tools in the Resource Hub.</EmptyState>
            ) : (
              <div className="grid grid-cols-2 gap-2.5">
                {resources.items.slice(0, 6).map((l) => (
                  <a key={l.id} href={l.url.startsWith("http") ? l.url : `https://${l.url}`} target="_blank" rel="noreferrer"
                    data-testid="quicklink-item"
                    className="flex items-center gap-2.5 p-3 rounded-2xl bg-slate-50 hover:bg-primary/8 transition-colors">
                    <span className="h-8 w-8 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0" style={{ background: l.color || "#8FA9E3" }}>
                      {l.title[0]?.toUpperCase()}
                    </span>
                    <span className="text-sm font-medium text-slate-700 truncate">{l.title}</span>
                  </a>
                ))}
              </div>
            )}
          </Widget>
        )}

        {/* AI drafting shortcut */}
        <Widget className="col-span-full md:col-span-2 lg:col-span-3 bg-gradient-to-br from-primary/12 via-white to-accent/20" delay={0.12} testId="widget-ai">
          <WidgetHeader icon={Sparkles} title="Smart Drafting" />
          <p className="text-sm text-slate-600 mb-4">Draft lesson ideas, outlines, and parent emails in seconds. You review before anything is used.</p>
          <Link to="/drafting"><Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="ai-open-button"><Sparkles size={16} className="mr-1.5" /> Open drafting studio</Button></Link>
        </Widget>

        {/* Recent lessons */}
        {show("lessons") && (
          <Widget className="col-span-full md:col-span-2 lg:col-span-3" delay={0.15} testId="widget-lessons">
            <WidgetHeader icon={NotebookPen} title="Recent Lesson Plans"
              action={<Link to="/planner"><Button variant="ghost" size="sm" className="rounded-full text-primary" data-testid="lessons-view">Planner</Button></Link>} />
            {lessons.items.length === 0 ? (
              <EmptyState>No plans yet. Head to the planner to create one.</EmptyState>
            ) : (
              <div className="space-y-2">
                {lessons.items.slice(-4).reverse().map((l) => (
                  <Link to="/planner" key={l.id} className="block p-3 rounded-2xl bg-slate-50 hover:bg-primary/8 transition-colors" data-testid="lesson-item">
                    <p className="font-semibold text-slate-800 text-sm truncate">{l.title}</p>
                    <p className="text-xs text-slate-400">{[l.subject, l.class_name, l.day_of_week].filter(Boolean).join(" · ") || l.plan_type}</p>
                  </Link>
                ))}
              </div>
            )}
          </Widget>
        )}

        {/* Notes */}
        {show("notes") && (
          <Widget className="col-span-full md:col-span-2 lg:col-span-3" delay={0.18} testId="widget-notes">
            <WidgetHeader icon={StickyNote} title="Sticky Notes"
              action={<QuickNote onAdd={(body) => notes.create(body)} />} />
            {notes.items.length === 0 ? (
              <EmptyState>Jot a quick note to your future self.</EmptyState>
            ) : (
              <div className="grid grid-cols-2 gap-2.5">
                {notes.items.slice(-4).reverse().map((n) => (
                  <div key={n.id} className="p-3 rounded-2xl group relative" style={{ background: `${n.color || "#F4C2C2"}44` }} data-testid="note-item">
                    {n.title && <p className="font-semibold text-slate-800 text-sm mb-0.5 truncate">{n.title}</p>}
                    <p className="text-xs text-slate-600 line-clamp-3">{n.content}</p>
                    <button onClick={() => notes.remove(n.id)} className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-destructive transition"><Trash2 size={13} /></button>
                  </div>
                ))}
              </div>
            )}
          </Widget>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tid }) {
  return (
    <div className="tcc-card p-5 flex items-center gap-4" data-testid={tid}>
      <div className="h-12 w-12 rounded-2xl bg-primary/12 flex items-center justify-center shrink-0">
        <Icon className="text-primary" strokeWidth={1.7} />
      </div>
      <div>
        <p className="font-heading text-2xl font-extrabold text-slate-800 leading-none">{value}</p>
        <p className="text-xs text-slate-500 mt-1">{label}</p>
      </div>
    </div>
  );
}

function QuickNote({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    await onAdd({ content: content.trim(), color: "#F4C2C2" });
    setContent("");
    setOpen(false);
    toast.success("Note added");
  };
  if (!open) return <Button variant="ghost" size="sm" className="rounded-full text-primary" data-testid="note-add-open" onClick={() => setOpen(true)}><Plus size={16} /></Button>;
  return (
    <form onSubmit={submit} className="flex gap-1">
      <Input autoFocus value={content} onChange={(e) => setContent(e.target.value)} placeholder="Note…" data-testid="note-input" className="h-8 w-32 rounded-lg text-sm" />
      <Button type="submit" size="icon" className="h-8 w-8 rounded-lg bg-primary" data-testid="note-save"><Check size={14} /></Button>
    </form>
  );
}
